import { Dispatch, SetStateAction, useCallback, useRef } from 'react';

import { ColumnSelectionItem } from '../components/templates/ColumnSelection';
import { applyColumnWidth, ColumnLike, clearSessionColumnState } from '../utils/sessionColumnState';

interface UseSessionColumnsArgs<T extends ColumnLike> {
  setColumns: Dispatch<SetStateAction<T[]>>;
  setColSelectionData: Dispatch<SetStateAction<ColumnSelectionItem[]>>;
  /** Returns the session-layered column defs. */
  buildColumns: () => T[];
  /** Returns the column-picker selection matching them. */
  buildColSelection: () => ColumnSelectionItem[];
  /**
   * sessionStorage key owner for this list. Required for
   * `restoreDefaultColumns`; omit it and that action is a no-op.
   */
  sessionPageName?: string;
}

interface UseSessionColumnsReturn {
  /** Restore the default view's own layout (browser session, else built defaults). */
  resetColumnsToDefault: () => void;
  /**
   * Discard this list's saved layout entirely and rebuild from the built-in
   * defaults — what the column picker's "Reset to default" means.
   *
   * Distinct from `resetColumnsToDefault`, which deliberately keeps the session
   * layout (widths included) when returning to the default *view*. Because the
   * builders layer sessionStorage on top of the defaults, resetting without
   * clearing first just rebuilds the state the user is trying to get rid of.
   */
  restoreDefaultColumns: () => void;
  /** Set a single column's width from a header resize-drag (freezes siblings via seed). */
  setColumnWidth: (key: string, width: number, seed?: Record<string, number>) => void;
}

/**
 * Shared column-layout actions for a list page — the `resetColumnsToDefault`
 * ("return to the default view") and the resize handler that were previously
 * copy-pasted across every list-data hook. The page owns the `columns` /
 * `colSelectionData` state and passes its setters in (so React's stable-setter
 * guarantee is preserved for the page's own callbacks); the builders stay
 * per-page because each resource has its own column set and picker shape.
 */
export const useSessionColumns = <T extends ColumnLike>({
  setColumns,
  setColSelectionData,
  buildColumns,
  buildColSelection,
  sessionPageName,
}: UseSessionColumnsArgs<T>): UseSessionColumnsReturn => {
  // Always call the latest builders (some are parameterised, e.g. by client
  // visibility) without making resetColumnsToDefault change identity.
  const buildColumnsRef = useRef(buildColumns);
  buildColumnsRef.current = buildColumns;
  const buildColSelectionRef = useRef(buildColSelection);
  buildColSelectionRef.current = buildColSelection;

  const resetColumnsToDefault = useCallback(() => {
    setColumns(buildColumnsRef.current());
    setColSelectionData(buildColSelectionRef.current());
  }, [setColumns, setColSelectionData]);

  const restoreDefaultColumns = useCallback(() => {
    if (!sessionPageName) return;
    // Clear first: the builders read sessionStorage, so they only return true
    // defaults once the saved layout is gone.
    clearSessionColumnState(sessionPageName);
    setColumns(buildColumnsRef.current());
    setColSelectionData(buildColSelectionRef.current());
  }, [sessionPageName, setColumns, setColSelectionData]);

  const setColumnWidth = useCallback(
    (key: string, width: number, seed?: Record<string, number>) => {
      setColumns((prev) => applyColumnWidth(prev, key, width, seed));
    },
    [setColumns]
  );

  return { resetColumnsToDefault, restoreDefaultColumns, setColumnWidth };
};
